import { get, post, put } from "aws-amplify/api";
import { default as React, useState, useEffect, useCallback, useMemo } from "react";
import { Alert, Button, Col, Row } from "react-bootstrap";
import { PacmanLoader } from "react-spinners";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import "containers/common/List.css";
import "containers/common/Table.css";
import { PAGE_SIZES } from "utils/pageSizeConfig";

import {
  createColumnHelper,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

function CountryList({ isAuthenticated, user }) {
  const countryIdPrefix = "country-list";

  const [isLoading, setIsLoading] = useState(true);
  const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
  const [sorting, setSorting] = useState([]);
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);

  // --- API Functions ---
  const getData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [countriesResponse, regionsResponse] = await Promise.all([
        get({ apiName: "colin", path: "/countries/all" }).response,
        get({ apiName: "colin", path: "/regions" }).response
      ]);

      const countriesJson = await countriesResponse.body.json();
      const regionsJson = await regionsResponse.body.json();

      const mappedRegions = regionsJson.map(r => ({
        label: String(r.region_code || r.label || Object.values(r)[0]),
        value: String(r.region_code || r.value || Object.values(r)[0])
      }));

      setRegions(mappedRegions || []);

      setCountries(countriesJson || []);
    } catch (err) {
      console.error("Fetch error:", err);
      setUi({ showAlert: true, errorMessage: "Failed to load data." });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) getData();
  }, [isAuthenticated, getData]);

  const saveRecord = async (record) => {
    const isNew = record.new_record;
    const path = isNew ? "/countries/new" : `/countries/${record.country_code}`;
    const method = isNew ? post : put;

    try {
      const result = await method({
        apiName: "colin",
        path: path,
        options: { body: { ...record, last_edited_by: user } }
      });
      return await result.response;
    } catch (err) {
      console.error("Save error:", err);
      throw err;
    }
  };

  // --- Table Logic ---
  const updateTableState = (rowIndex, columnId, value) => {
    setCountries(old => old.map((record, index) => (
      index === rowIndex
        ? { ...record, [columnId]: value, modified: true }
        : record
    )));
  };

  const isValidRegionCode = (regionCode) => {
    const normalizedRegionCode = String(regionCode ?? "").trim();
    if (normalizedRegionCode === "") {
      return false;
    }

    if (!Array.isArray(regions) || regions.length === 0) {
      return true;
    }

    return regions.some(r => String(r.value ?? "").trim() === normalizedRegionCode);
  };

  const validateRecord = (record) => {
    if (!record.country_code || String(record.country_code).trim() === "") {
      return [false, "Code cannot be blank. The record has not been saved"];
    }
    if (!record.country || String(record.country).trim() === "") {
      return [false, "Country cannot be blank. The record has not been saved"];
    }
    if (!record.region_code || String(record.region_code).trim() === "") {
      return [false, "Region cannot be blank. The record has not been saved"];
    }
    if (!isValidRegionCode(record.region_code)) {
      return [false, "Region must be selected from the dropdown. The record has not been saved"];
    }
    return [true, ""];
  };

  const getRequiredFieldValidationMessage = (fieldId, fieldValue) => {
    const normalizedFieldValue = String(fieldValue ?? "").trim();

    if (fieldId === "country_code" && normalizedFieldValue === "") {
      return "Code cannot be blank. The record has not been saved";
    }

    if (fieldId === "country" && normalizedFieldValue === "") {
      return "Country cannot be blank. The record has not been saved";
    }

    if (fieldId === "region_code") {
      if (normalizedFieldValue === "") {
        return "Region cannot be blank. The record has not been saved";
      }
      if (!isValidRegionCode(normalizedFieldValue)) {
        return "Region must be selected from the dropdown. The record has not been saved";
      }
    }

    return "";
  };

  // --- Save Logic Functions ---
  const handleSave = async ({ row, column, value }) => {
    const rowIndex = row.index;
    const recordToSave = { ...row.original, [column.id]: value };
    const [isValid, message] = validateRecord(recordToSave);

    setUi(prev => ({ ...prev, showAlert: false, errorMessage: "" }));

    if (recordToSave.new_record) {
      const fieldMessage = getRequiredFieldValidationMessage(column.id, String(recordToSave[column.id] ?? "").trim());
      if (fieldMessage) {
        setUi({ showAlert: true, errorMessage: fieldMessage });
        return true;
      }

      if (!isValid) {
        setUi({ showAlert: true, errorMessage: message });
        return true;
      }
    }

    if (!isValid) {
      setUi({ showAlert: true, errorMessage: message });
      return true;
    }

    if (recordToSave.isSaving) return true;

    setCountries(old => old.map((r, idx) =>
      idx === rowIndex ? { ...r, isSaving: true, [column.id]: value } : r
    ));

    try {
      await saveRecord(recordToSave);
      setCountries(old => old.map((r, idx) =>
        idx === rowIndex
          ? { ...r, [column.id]: value, modified: false, isSaving: false, new_record: false }
          : r
      ));
      return true;
    } catch (err) {
      setCountries(old => old.map((r, idx) => idx === rowIndex ? { ...r, isSaving: false } : r));
      setUi({ showAlert: true, errorMessage: "Network Error: Could not save record." });
      return false;
    }
  };

  const addRecord = () => {
    setCountries(prev => [{
      country_code: "",
      country: "",
      region_code: "",
      created_by: user,
      new_record: true,
      modified: true
    }, ...prev]);
  };

  // --- Column Definitions ---
  const columns = useMemo(() => [
    columnHelper.accessor("country_code", {
      id: "country_code",
      header: "Code",
      cell: EditableCell,
      size: 30,
    }),
    columnHelper.accessor("country", {
      id: "country",
      header: "Country",
      cell: EditableCell,
      size: 50,
    }),
    columnHelper.accessor("region_code", {
      id: "region_code",
      header: "Region",
      cell: (props) => {
        return <EditableCell {...props} options={regions} dropdownWidth="150px" />;
      },
      enableSorting: false,
    }),
  ], [regions]);

  const createId = useMemo(
    () => createElementIdFactory(countryIdPrefix),
    [countryIdPrefix]
  );

  const tableElementIds = useMemo(
    () => createReusableTableElementIds(createId),
    [createId]
  );

  if (!isAuthenticated) return null;

  // --- Render Functions --- 
  return (
    <div className="mt-3">
      <Alert show={ui.showAlert} variant="danger" onClose={() => setUi({ ...ui, showAlert: false })} dismissible>
        {ui.errorMessage}
      </Alert>
      <Row className="mb-2 mx-0">
        <Col className="text-right" style={{ marginBottom: '12px' }}>
          <Button id={createId("add", "button")} variant="secondary" size="sm" onClick={addRecord}>Add</Button>
        </Col>
      </Row>

      <Row className="mx-0">
        <Col className="p-0">
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader
                  cssOverride={{ display: "block", margin: "0 auto" }}
                  size={20}
                  color={"#e03c31"}
                  loading={isLoading}
                />
              </div>
            </div>
          ) : (
            <div className="custom-data-table" style={{ width: "100%" }}>
              <GenericTable
                columns={columns}
                data={countries}
                section="country-list"
                elementIds={tableElementIds}
                pageSize={PAGE_SIZES.country}
                enablePagination={true}
                state={{ sorting }}
                onSortingChange={setSorting}
                enableSortingRemoval={false}
                meta={{
                  updateData: updateTableState,
                  handleSave: handleSave,
                  shouldForceSaveOnBlur: ({ row, column, value }) => (
                    !!row?.original?.new_record
                    && ["country_code", "country", "region_code"].includes(column?.id)
                    && String(value ?? "").trim() === ""
                  ),
                }}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default CountryList;